import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { LockKey, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { API } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMessage({ text: data.message, type: 'success' });
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setMessage({ text: err.response?.data?.detail || "Failed to reset password", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-destructive font-bold uppercase tracking-widest">
        INVALID OR MISSING RECOVERY TOKEN
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full opacity-20 mix-blend-screen bg-gradient-to-br from-chart-4/30 to-background/5" />
      </div>
      <Card className="w-full max-w-md z-10 rounded-none border-white/10 bg-black/60 backdrop-blur-xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto bg-chart-4/20 p-4 rounded-full w-20 h-20 flex items-center justify-center border border-chart-4/50">
            <LockKey size={40} weight="duotone" className="text-chart-4" />
          </div>
          <CardTitle className="font-heading text-3xl">SET NEW CREDENTIALS</CardTitle>
          <CardDescription className="text-muted-foreground uppercase tracking-widest font-bold text-xs">Enter your new secure password below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {message && (
              <div className={`p-3 text-sm font-bold border ${message.type === 'success' ? 'bg-chart-5/10 border-chart-5/20 text-chart-5' : 'bg-destructive/10 border-destructive/20 text-destructive'}`}>
                {message.text}
              </div>
            )}
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"} 
                placeholder="NEW PASSWORD" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                required 
                className="h-12 bg-black/50 border-white/10 rounded-none text-white tracking-wider pr-10" 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-14 font-heading text-xl tracking-wider rounded-none bg-chart-4 text-background hover:bg-chart-4/80 font-black">
              {loading ? 'UPDATING...' : 'CONFIRM NEW PASSWORD'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}